import { DownOutlined } from '@ant-design/icons';
import { Button as BaseButton, Dropdown, Input, message, Modal } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';
import { PanelViews } from 'types/PanelViews';
import { last } from 'utils/Array';
import {
  normalizeSpace,
  pluralEnglish,
  removeSpecialCharacters,
} from 'utils/String';

const MENU_BUTTON_LABEL = 'Actions';

const Button = styled(BaseButton)`
  padding: 0 10px;
`;

function confirmRemoveAdsMessage(count: number | undefined) {
  // These first two should not be visible to the end user. Calling
  // environment should catch these possibilities before it gets to this
  // point, but we allow them as arguments for ease of caller use.
  if (count == null) return '⚠️ No current creative';
  if (count === 0) return '⚠️ No ads selected';
  const ads = pluralEnglish(count, 'ad');
  return `Are you sure you want to permanently remove ${count} ${ads}?`;
}

function SelectionMenu() {
  const [confirmRemoveAdVisible, setConfirmRemoveAdVisible] = useState(false);
  const [confirmMakeBundleVisible, setConfirmMakeBundleVisible] =
    useState(false);
  const [confirmMakeVideoBundleVisible, setConfirmMakeVideoBundleVisible] =
    useState(false);
  const [confirmMakeTearsheetVisible, setConfirmMakeTearsheetVisible] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [videoBundleName, setVideoBundleName] = useState('');
  const [tearsheetName, setTearsheetName] = useState('');

  const store_creative = store.creative;

  const hasSelection = store_creative?.ads.all.some((a) => a.selected);

  const handleNewTearsheet = async () => {
    if (!store_creative) return;
    setLoading(true);
    try {
      const response = await store_creative.tearsheets.new(
        tearsheetName,
        store_creative.ads.selected
      );
      console.log('response', response);
      // Not awaited. antd's MessageType is a PromiseLike that settles when the
      // message *closes* - three seconds by default - so awaiting these would
      // hold the modal open on its spinner for the whole reading delay before
      // closing and switching panel.
      if (response.error) {
        void message.error(response.error);
        // TODO: Should we early-out here? Otherwise we navigate to the
        //  tearsheet for error outcomes as well, which is inconsistent, but
        //  could be justified if a tearsheet can partially succeed. Look
        //  into this.
      } else {
        void message.success('Tearsheet created successfully');
      }
      store_creative.setPanel(PanelViews.TEARSHEETS);
      // Deliberately inside the try rather than the finally: a throw should
      // leave the modal open with the name still in it, so the user can retry.
      // Only `loading` needs resetting either way.
      setConfirmMakeTearsheetVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAds = async () => {
    if (!store_creative) return;
    setLoading(true);
    try {
      await store_creative.ads.removeMultipleAds(store_creative.ads.selected);
      setConfirmRemoveAdVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateAds = () => {
    if (!store_creative) return;
    const ads = store_creative.ads.selected.map((ad) => {
      return ad.cloneData();
    });
    const lastAd = last(ads);
    const insertIndex = lastAd ? lastAd.index + 1 : 0;
    store_creative.ads.addMultipleAds(ads, insertIndex);
  };

  const handleBundleAds = async () => {
    if (!store_creative) return;
    setLoading(true);
    try {
      await store_creative.bundles.new(
        normalizeSpace(bundleName),
        store_creative.ads.selected
      );
      store_creative.setPanel(PanelViews.BUNDLES);
      setConfirmMakeBundleVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoBundleAds = async () => {
    if (!store_creative) return;
    setLoading(true);
    try {
      await store_creative.videoBundles.new(
        normalizeSpace(videoBundleName),
        store_creative.ads.selected
      );
      store_creative.setPanel(PanelViews.VIDEO_BUNDLES);
      setConfirmMakeVideoBundleVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCSVExport = () => {
    if (!store_creative) return;
    store_creative.ads.exportAndDownloadCSV();
  };

  const handleCSVImport = async () => {
    if (!store_creative) return;
    try {
      await store_creative.ads.importCSV();
    } catch (err: any) {
      console.error('[SelectionMenu.handleCSVImport]', err);
      void message.error(err.message, 10);
    }
  };

  const handleSaveSelected = async () => {
    if (!store_creative) return;
    await store_creative.ads.updateMultiple(store_creative.ads.selected);
    void message.success('Ads successfully saved.', 5);
  };

  const handleRevertSelected = () => {
    store_creative?.ads.selected.forEach((ad) => ad.properties.revert());
  };

  const handleSelectChanged = () => {
    store_creative?.ads.selectChanged();
  };

  const menuSeed = [
    {
      label: 'Select Changed',
      enabled: store_creative && store_creative.ads.length > 0,
      onClick: () => handleSelectChanged(),
    },
    {
      label: 'Save Selected',
      enabled: hasSelection,
      onClick: () => handleSaveSelected(),
    },
    {
      label: 'Revert Selected',
      enabled: hasSelection,
      onClick: () => handleRevertSelected(),
    },
    {
      label: 'Remove Selected',
      enabled: hasSelection,
      onClick: () => setConfirmRemoveAdVisible(true),
    },
    {
      label: 'Duplicate Selected',
      enabled: hasSelection,
      onClick: () => handleDuplicateAds(),
    },
    {
      label: 'Create Bundle',
      enabled: hasSelection,
      onClick: () => setConfirmMakeBundleVisible(true),
    },
    {
      label: 'Create Video Bundle',
      enabled: hasSelection,
      onClick: () => setConfirmMakeVideoBundleVisible(true),
    },
    {
      label: 'Create Tearsheet',
      enabled: hasSelection,
      onClick: () => setConfirmMakeTearsheetVisible(true),
    },
    {
      label: 'Download CSV',
      enabled: hasSelection,
      onClick: () => handleCSVExport(),
    },
    {
      label: 'Import CSV',
      enabled: true,
      onClick: () => handleCSVImport(),
    },
  ];

  const menuItems = menuSeed.map((s) => ({
    label: s.label,
    key: s.label,
    disabled: !s.enabled,
    onClick: s.onClick,
  }));

  return (
    <>
      <Dropdown trigger={['click']} menu={{ items: menuItems }}>
        <Button>
          <DownOutlined />
          {MENU_BUTTON_LABEL}
        </Button>
      </Dropdown>

      <Modal
        title="Remove ads"
        open={confirmRemoveAdVisible}
        onOk={handleRemoveAds}
        confirmLoading={loading}
        onCancel={() => {
          setConfirmRemoveAdVisible(false);
          setLoading(false);
        }}
        okText="Yes"
        cancelText="No"
      >
        <p>{confirmRemoveAdsMessage(store_creative?.ads.selected.length)}</p>
      </Modal>

      <Modal
        title="Create Bundle"
        open={confirmMakeBundleVisible}
        okButtonProps={{ disabled: !bundleName }}
        onOk={handleBundleAds}
        confirmLoading={loading}
        onCancel={() => {
          setConfirmMakeBundleVisible(false);
          setLoading(false);
        }}
        okText="Create"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter a bundle name"
          value={bundleName}
          onChange={(e) =>
            setBundleName(removeSpecialCharacters(e.target.value))
          }
        ></Input>
      </Modal>

      <Modal
        title="Create Video Bundle"
        open={confirmMakeVideoBundleVisible}
        okButtonProps={{ disabled: !videoBundleName }}
        onOk={handleVideoBundleAds}
        confirmLoading={loading}
        onCancel={() => {
          setConfirmMakeVideoBundleVisible(false);
          setLoading(false);
        }}
        okText="Create"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter a video bundle name"
          value={videoBundleName}
          onChange={(e) =>
            setVideoBundleName(removeSpecialCharacters(e.target.value))
          }
        ></Input>
      </Modal>

      <Modal
        title="Create Tearsheet"
        open={confirmMakeTearsheetVisible}
        okButtonProps={{ disabled: !tearsheetName }}
        onOk={handleNewTearsheet}
        confirmLoading={loading}
        onCancel={() => {
          setConfirmMakeTearsheetVisible(false);
          setLoading(false);
        }}
        okText="Create"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter a bundle name"
          value={tearsheetName}
          onChange={(e) =>
            setTearsheetName(removeSpecialCharacters(e.target.value))
          }
        ></Input>
      </Modal>
    </>
  );
}

export default observer(SelectionMenu);
